import type { ReactNode } from 'react'
import type { Appointment } from '@/types/clinic'

import { useState } from 'react'
import { useRouter } from 'next/router'
import { toast } from 'sonner'
import {
    CalendarX2,
    ClipboardPlus,
    Ellipsis,
    MessageCircle,
    Pencil,
    RefreshCw,
    Send,
    Trash2,
    UserRound
} from 'lucide-react'
import { useClinic } from '@/lib/clinic/store'
import { formatDate, formatTime } from '@/lib/dates'
import { useAppDialogs } from '@/components/dialogs/app-dialogs'
import { Button } from '@/components/ui/button'
import ConfirmDialog from '@/components/app/confirm-dialog'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

interface AppointmentActionsMenuProps {
    appointment: Appointment
}

const AppointmentActionsMenu = ({ appointment }: AppointmentActionsMenuProps): ReactNode => {
    const router = useRouter()
    const { data, actions } = useClinic()
    const { openAppointment, openVisit, openWhatsApp } = useAppDialogs()
    const [confirmDelete, setConfirmDelete] = useState(false)

    const patient = data.patients.find(item => item.id === appointment.patientId)
    const hasVisit = data.visits.some(item => item.appointmentId === appointment.id)
    const closed = appointment.status === 'cancelled' || appointment.status === 'completed'

    const cancel = () => {
        const previous = appointment.status
        actions.setAppointmentStatus(appointment.id, 'cancelled')
        toast.success('Appointment cancelled', {
            description: `${patient?.name ?? 'Patient'}, ${formatDate(appointment.date)} at ${formatTime(appointment.time)}`,
            action: { label: 'Undo', onClick: () => actions.setAppointmentStatus(appointment.id, previous) }
        })
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger render={<Button variant='ghost' size='icon-sm' aria-label={`Actions for ${patient?.name ?? 'appointment'}`} />}>
                    <Ellipsis aria-hidden='true' />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-56'>
                    {patient && (
                        <DropdownMenuItem onClick={() => router.push(`/patients/${patient.id}`)}>
                            <UserRound aria-hidden='true' />
                            View patient
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => openAppointment({ appointmentId: appointment.id })}>
                        <Pencil aria-hidden='true' />
                        Edit appointment
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openAppointment({ appointmentId: appointment.id, reschedule: true })}>
                        <RefreshCw aria-hidden='true' />
                        Reschedule
                    </DropdownMenuItem>
                    {!hasVisit && appointment.status !== 'cancelled' && (
                        <DropdownMenuItem onClick={() => openVisit({ patientId: appointment.patientId, appointmentId: appointment.id })}>
                            <ClipboardPlus aria-hidden='true' />
                            Record visit
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => openWhatsApp({ patientId: appointment.patientId, kind: 'appointmentConfirmation', appointmentId: appointment.id })}>
                        <Send aria-hidden='true' />
                        Send confirmation
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openWhatsApp({ patientId: appointment.patientId, kind: 'appointmentReminder', appointmentId: appointment.id })}>
                        <MessageCircle aria-hidden='true' />
                        Send reminder
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {!closed && (
                        <DropdownMenuItem onClick={cancel}>
                            <CalendarX2 aria-hidden='true' />
                            Cancel appointment
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem variant='destructive' onClick={() => setConfirmDelete(true)}>
                        <Trash2 aria-hidden='true' />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ConfirmDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title='Delete this appointment?'
                description={`${patient?.name ?? 'This patient'} on ${formatDate(appointment.date)} at ${formatTime(appointment.time)} will be removed. To keep a record, cancel it instead.`}
                confirmLabel='Delete appointment'
                onConfirm={() => {
                    actions.deleteAppointment(appointment.id)
                    toast.success('Appointment deleted')
                }}
            />
        </>
    )
}

export default AppointmentActionsMenu
